import {useState} from 'react';

import {useApolloClient} from '@apollo/client';
import {addToast} from '@albert-io/atomic';

import {GoogleClassroomConnectionDocument} from 'client/components/Google/GoogleClassroomConnection.queries.generated';
import {startGoogleClassroomConsent} from 'client/components/Google/googleClassroomConsent';
import {invalidatePartialInterest} from 'resources/mandark.resource';

import {
  syncGoogleCourseworkQuery,
  unsyncGoogleCourseworkQuery
} from './googleClassroomSync.queries';

export interface UseGoogleClassroomSyncResult {
  isSynced: boolean;
  isRequestPending: boolean;
  /** The teacher has no working Google Classroom connection, so the cell should offer Reconnect. */
  needsReconnect: boolean;
  sync: () => Promise<void>;
  unsync: () => Promise<void>;
  /** Sends the teacher back through the Google Classroom connect flow. */
  reconnect: () => void;
}

interface GoogleClassroomSyncError {
  response?: {body?: {error?: string}};
}

/**
 * Per-assignment Google Classroom sync state and actions for one classroom.
 * `hasGoogleCourseworkId` (the assignment's stored coursework id) is the synced state until a
 * sync or unsync in this session overrides it; each change also refetches the assignment so the
 * stored id catches up. `googleClassroomConnected` is the backend's answer about the teacher
 * (`undefined` while unknown) and the only source of `needsReconnect`; a `not_connected` refusal
 * refetches that answer, so every cell reading it flips together.
 */
export const useGoogleClassroomSync = (
  assignmentId: string,
  classroomId: string,
  hasGoogleCourseworkId: boolean,
  googleClassroomConnected?: boolean
): UseGoogleClassroomSyncResult => {
  const client = useApolloClient();
  const [syncedOverride, setSyncedOverride] = useState<boolean | null>(null);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const isSynced = syncedOverride ?? hasGoogleCourseworkId;
  const needsReconnect = googleClassroomConnected === false;

  const refreshAssignment = () => {
    invalidatePartialInterest({resourcePath: ['assignments_v3', assignmentId]});
  };

  const handleSyncError = async (e: unknown) => {
    const errorCode = (e as GoogleClassroomSyncError)?.response?.body?.error;
    if (errorCode === 'not_connected') {
      addToast({
        color: 'negative',
        message: 'Google Classroom is no longer connected. Reconnect to keep syncing.'
      });
      try {
        await client.refetchQueries({include: [GoogleClassroomConnectionDocument]});
      } catch {
        // The toast already said what happened; the cells follow on the next load.
      }
      return;
    }
    if (errorCode === 'classroom_not_synced') {
      addToast({
        color: 'negative',
        message: 'Import this classroom from Google Classroom before syncing assignments.'
      });
      return;
    }
    addToast({
      color: 'negative',
      message: 'There was an error syncing with Google Classroom. Please try again.'
    });
  };

  const sync = async () => {
    setIsRequestPending(true);
    try {
      await syncGoogleCourseworkQuery(classroomId, assignmentId);
      setSyncedOverride(true);
      refreshAssignment();
      addToast({
        color: 'positive',
        title: 'Success!',
        message: 'Your assignment is synced with Google Classroom.'
      });
    } catch (e) {
      await handleSyncError(e);
    } finally {
      setIsRequestPending(false);
    }
  };

  const unsync = async () => {
    setIsRequestPending(true);
    try {
      // A transient Google failure is a 200 that RETAINS the sync (so the
      // teacher can retry) — the response's synced attribute is the truth.
      const response = await unsyncGoogleCourseworkQuery(classroomId, assignmentId);
      const stillSynced = !!response?.body?.data?.attributes?.synced;
      setSyncedOverride(stillSynced);
      if (!stillSynced) {
        refreshAssignment();
      }
      if (stillSynced) {
        addToast({
          color: 'negative',
          message: 'Google Classroom is temporarily unavailable. Please try unsyncing again.'
        });
      } else {
        addToast({
          message: 'Your assignment is no longer synced with Google Classroom.'
        });
      }
    } catch (e) {
      await handleSyncError(e);
    } finally {
      setIsRequestPending(false);
    }
  };

  return {
    isSynced,
    isRequestPending,
    needsReconnect,
    sync,
    unsync,
    reconnect: startGoogleClassroomConsent
  };
};
